import React, { useEffect, useState } from 'react';
import { db, appShopifyHandoff, storage } from '../firebase';
import { doc, getDoc, setDoc, collection, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from './authContext';
import { Button, CircularProgress, TextField, MenuItem, Select, InputLabel, FormControl, Box, Typography } from '@mui/material';
import { conditionValues, conditionToValueMap, constants, getDropdownOptions, translateDropdownOptions } from './productEditConstants';
import { customerHasShopify, customerHasEbay, customerHasGoogleMerchant, customerHasEtsy } from './sharedUtils';
import { Alert } from '@mui/material';
import { postProductToChannel, deleteProductFromChannel, addProductImages } from './api';
import { encodeFileAsBase64, resizeImage, extractErrorMessage, isHeicFile, convertHEICToJPEG } from './sharedUtils';
import { useNavigate } from 'react-router-dom';
import { SHOPIFY_CATEGORY_NAMES, SHOPIFY_CATEGORY_OPTIONS } from './products/shopifyCategoryOptions';

const inflect = require('inflection');

const ProductEditGeneric = ({ customerId, productId, updateState, uploaderStyle, groupIndex }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({});
  const [imageUrls, setImageUrls] = useState([]);
  const [formatPrefs, setFormatPrefs] = useState({});
  const [customFields, setCustomFields] = useState({});
  const [missingRequiredFields, setMissingRequiredFields] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState({})
  const { customer, shopikUser } = useAuth(); // Assuming 'customer' contains user data
  const [shopifyUrl, setShopifyUrl] = useState(null);
  const [ebayUrl, setEbayUrl] = useState(null);
  const [etsyUrl, setEtsyUrl] = useState(null);
  const [gmcPosted, setGmcPosted] = useState(false);
  const [gmcMerchantId, setGmcMerchantId] = useState(null);
  const [error, setError] = useState(null);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const addPhotoInputRef = React.useRef(null);
  const [upc, setUPC] = useState(null);
  const [sku, setSku] = useState(null);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [shopifyCat, setShopifyCat] = useState('');
  const [ebayCat, setEbayCat] = useState('');
  const [ebayCategories, setEbayCategories] = useState([]);
  const navigate = useNavigate();
  
  const handleAddPhotos = () => {
    addPhotoInputRef.current.value = null;
    addPhotoInputRef.current.click();
  };

  const handlePhotoFilesSelected = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setIsUploadingPhotos(true);
    setError(null);
    try {
      const images = await Promise.all(
        files.map(async (file) => {
          let convertedFile = file;
          if (isHeicFile(file)) {
            const jpegBlob = await convertHEICToJPEG(file);
            if (jpegBlob) {
              convertedFile = new File([jpegBlob], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
            }
          }
          const resized = await resizeImage(convertedFile);
          return encodeFileAsBase64(resized);
        })
      );
      await addProductImages(customer.apiKey, productId, images);
    } catch (err) {
      setError(err.message || 'Failed to upload photos');
    } finally {
      setIsUploadingPhotos(false);
    }
  };

  const addNewField = () => {
    if (newFieldKey.trim() && newFieldValue.trim()) {
      setFormData({
        ...formData,
        [newFieldKey]: newFieldValue
      });
      setNewFieldKey('');
      setNewFieldValue('');
    }
  };
  // Function to handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    formData[name] = value;
    let title = updateTitle(formData);
    let description = updateDescription(formData);
    setFormData({
      ...formData,
      ['Title']: title,
      ['Description']: description,
      [name]: value,
    });
  };

const getKeyByValue = (obj, targetValue) => {
  return Object.keys(obj).find(key => obj[key] === targetValue);
};

  // Function to handle dropdown changes
  const handleDropdownChange = (e) => {
    var { name, value } = e.target;
    if (name === 'Condition') {
      value = getKeyByValue(conditionToValueMap, value)
    }
    setFormData((prevFormData) => {
      let updatedFormData = { ...prevFormData, [name]: value };

      // if (name === 'Category') {
      //   // Remove any existing fields that shouldn't be in formData based on category change
      //   Object.keys(categoryToFieldsMap).forEach((category) => {
      //     categoryToFieldsMap[category].forEach((field) => {
      //       if (updatedFormData.hasOwnProperty(field) && category !== value) {
      //         delete updatedFormData[field];
      //       }
      //     });
      //   });

      //   // Add relevant fields to formData based on the selected category
      //   const fieldsForCategory = categoryToFieldsMap[value] || [];
      //   fieldsForCategory.forEach((field) => {
      //     if (!updatedFormData.hasOwnProperty(field)) {
      //       updatedFormData[field] = '';
      //     }
      //   });
      // }

      return updatedFormData;
    });
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!areDropdownsFilled()) {
      setMissingRequiredFields(true);
      return;
    }

    const dbUserRef = collection(db, 'customers');
    const userRef = doc(dbUserRef, customerId);
    const productsRef = collection(userRef, 'products');
    const pRef = doc(productsRef, productId);
    const productData = (await getDoc(pRef)).data();
    var existingData = productData.consolidatedData;

    for (const key in formData) {
      if (key  === 'Category' && formData[key] !== existingData[key]) {
        //TODO retry and exit
      }
      existingData[key] = formData[key];
    }
    const extraFields = {};
    if (shopifyCat) {
      const nameParts = shopifyCat.split(' > ');
      extraFields.shopifyCategory = {
        categoryId: SHOPIFY_CATEGORY_OPTIONS[shopifyCat] || '',
        fullName: shopifyCat,
        name: nameParts[nameParts.length - 1],
      };
    }
    if (ebayCat) {
      extraFields.gptCategories = { primaryCategory: ebayCat, allPotentialCategories: ebayCategories };
    }
    await setDoc(pRef, { consolidatedData: existingData, sku: sku, upc: upc, ...extraFields }, { merge: true });
  };

  const areDropdownsFilled = () => {
    return true;
    // if (uploaderStyle !== 1) {
    //   return true;
    // }
    // return Object.keys(dropdownFields).every((key) => {
    //   const value = formData[key];
    //   return value !== 'Select an option' && value !== 'Not Available' && value !== '';
    // });
  };

  const viewProductPricing = () => {
    navigate('/product-pricing/' + customerId + '/' + productId)
  };
  
  function buildSpecs(specs) {
    const addAfter = [];
    const newArray = [];
    const dropdownOptions = {};
    const map = specs['consolidatedData']
    newArray.push(['Title', map['Title']])
    newArray.push(['Description', map['Description']])
    if (map.Price) {
      newArray.push(['Price', map['Price']])
    }
    if (map.Category) {
      newArray.push(['Category', map['Category']])
    } 
    if (map.Condition) {
      newArray.push(['Condition', map['Condition']])
    }
    // newArray.push(['eBay Category', map['Category']])
    for (const item in map) {
      // if (item === 'Category') {
      //   continue
      // }
      if (getDropdownOptions(item, specs).length > 0) {
        dropdownOptions[item] = getDropdownOptions(item, specs)
      }
      var value = map[item]
      if (!constants.EXCLUDE_FROM_PRODUCT_TERMS.includes(value.toLowerCase())) {
        newArray.push([item, value])
      } else {
        addAfter.push(item);
      }
    }
  
    for (const item of addAfter) {
      var value = map[item]
      newArray.push([item, value])      
    }
    const specDict = {};
    for (const item of newArray) {
      specDict[item[0]] = item[1]
    }
    setDynamicOptions(dropdownOptions)
    return specDict;
  }

  function updateTitle(consolidatedData) {
    if (formatPrefs.hasOwnProperty('titleFormat')) {
      let baseTitle = '';
      formatPrefs.titleFormat.split(',').forEach((field) => {
        if (consolidatedData.hasOwnProperty(field)) {
          if (consolidatedData[field] !== 'Unknown') {
            baseTitle += consolidatedData[field].replace(/,/g, ' and ') + ' ';
          }
        } else if (field.includes('literal')) {
          baseTitle += field.replace('literal', '') + ' ';
        }
      });
      baseTitle = baseTitle.trim();
      baseTitle = singularizeLastWord(baseTitle);
      if (baseTitle === '') {
        baseTitle = consolidatedData['Title'];
      }
      return baseTitle;
    }
    return consolidatedData['Title'];
  }

  function updateDescription(consolidatedData) {
    if (formatPrefs.hasOwnProperty('descriptionFormat')) {
      let baseDescription = '';
      formatPrefs.descriptionFormat.split(',').forEach((field) => {
        if (consolidatedData.hasOwnProperty(field)) {
          baseDescription += `${field}: ${consolidatedData[field]}<br>`;
        } else if (customFields.hasOwnProperty(field)) {
          baseDescription += `${field}: ${customFields[field]}<br>`;
        }
      });
      return baseDescription;
    }
    return consolidatedData['Description'];
  }

  function singularizeLastWord(title) {
    const words = title.split(' ');
    const word = words[words.length - 1];
    const singularWord = inflect.singularize(word);
    words[words.length - 1] = singularWord;
    return words.join(' ');
  }

  const toggleImageAtIndex = (index) => {
    setImageUrls((prevUrls) =>
      prevUrls.map((url, i) =>
        i === index
          ? url.includes('noBackground')
            ? url.replace('noBackground', 'original') // Toggle to original
            : url.replace('original', 'noBackground') // Toggle to noBackground
          : url
      )
    );
  };

  const handleShopifyPost = async (e) => {  
    setIsSubmitting(true)
    handleSubmit(e)

    try {
      const response = await postProductToChannel(customer.apiKey, productId, 'shopify', {});
      setShopifyUrl(response.uri)
      setError(null); // Clear error on success
    } catch (err) {
        setError(extractErrorMessage(err, 'Failed to post product to Shopify'));
    }
    setIsSubmitting(false)
  }

  const handleGoogleMerchantPost = async (e) => {
    setIsSubmitting(true);
    handleSubmit(e);
    try {
      const response = await postProductToChannel(customer.apiKey, productId, 'google_merchant', {});
      setGmcPosted(true);
      setGmcMerchantId(response.merchantId || null);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to post product to Google Merchant'));
    }
    setIsSubmitting(false);
  };

  const handleGoogleMerchantRemove = async () => {
    setIsSubmitting(true);
    try {
      await deleteProductFromChannel(customer.apiKey, productId, 'google_merchant');
      setGmcPosted(false);
      setGmcMerchantId(null);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to remove product from Google Merchant'));
    }
    setIsSubmitting(false);
  };

  const handleEbayPost = async (e) => {
    setIsSubmitting(true)
    handleSubmit(e)

    try {
      const response = await postProductToChannel(customer.apiKey, productId, 'ebay', {});
      setEbayUrl(process.env.REACT_APP_EBAY_PRODUCT_URL + '/' + response.listingId)
      setError(null); // Clear error on success
    } catch (err) {
        setError(extractErrorMessage(err, 'Failed to post product to eBay'));
    }
    setIsSubmitting(false)
  }

  const handleEtsyPost = async (e) => {
    setIsSubmitting(true)
    handleSubmit(e)

    try {
      const response = await postProductToChannel(customer.apiKey, productId, 'etsy', {});
      setEtsyUrl('https://www.etsy.com/listing/' + response.listingId)
      setError(null); // Clear error on success
    } catch (err) {
        setError(extractErrorMessage(err, 'Failed to post product to Etsy'));
    }
    setIsSubmitting(false)
  }

  useEffect(() => {
    const custColRef = collection(db, "customers");
    const custDocRef = doc(custColRef, customerId);
    const prodColRef = collection(custDocRef, "products");
    const prodDocRef = doc(prodColRef, productId);
    
    setupFormatting(customerId, productId);
    
    const unsub = onSnapshot(prodDocRef, async (productDoc) => {
      const data =  productDoc.data();
      if (data['consolidatedData']) {
        const specDict = buildSpecs(data);
        setFormData(specDict);
        setIsLoading(false);
        if (data.shopifyArray) {
          setShopifyUrl(data.shopifyArray[0].uri)
        }
        if (data.eBayArray) {
          setEbayUrl(process.env.REACT_APP_EBAY_PRODUCT_URL + '/' + data.eBayArray[0].listingId)
        }
        if (data.etsyArray?.length > 0 && data.etsyArray[0].listingId) {
          setEtsyUrl('https://www.etsy.com/listing/' + data.etsyArray[0].listingId)
        }
        if (data.googleMerchantArray?.length > 0) {
          setGmcPosted(true);
          setGmcMerchantId(data.googleMerchantArray[0].merchantId || null);
        }
        if (data.shopifyCategory?.fullName) {
          setShopifyCat(data.shopifyCategory.fullName);
        }
        if (data.gptCategories) {
          setEbayCat(data.gptCategories.primaryCategory || '');
          setEbayCategories(data.gptCategories.allPotentialCategories || []);
        }
        unsub(); // Unsubscribe after loading specs
      } 
      if (data['upc']) {
        setUPC(data.upc)
      }
      if (data['sku']) {
        setSku(data.sku)
      }
      if (data['mainImages']) {
        setImageUrls(data.mainImages.filter(image => !image.isHidden).map(image => image.urlString));
      }
    });
  
    // Cleanup function to unsubscribe when component unmounts or customerId or productId change
    return () => {
      unsub();
    };
  }, [customerId, productId]); // Add customerId and productId to dependency array

  const setupFormatting = async (customerId, productId) => {
    const custColRef = collection(db, "customers");
    const custDocRef = doc(custColRef, customerId);
    const prodColRef = collection(custDocRef, "products");
    const prodDocRef = doc(prodColRef, productId);
    const custDict = (await getDoc(custDocRef)).data();
    const productDict = (await getDoc(prodDocRef)).data();
    
    let formatData = {};
    let customFields = {};

    if (custDict.hasOwnProperty('outputPrefs')) {
        formatData = custDict.outputPrefs;
        setFormatPrefs(formatData);
        if (formatData.hasOwnProperty('customUserInfo') && productDict.hasOwnProperty('uploadedBy')) {
            let userId = productDict.uploadedBy.replace('localDebugDelete', '');
            const userColRef = collection(db, "users");
            const userDocRef = doc(userColRef, userId);
            const userDict = (await getDoc(userDocRef)).data();

            for (let field in formatData.customUserInfo) {
                let valueString = formatData.customUserInfo[field];
                let baseValue = '';

                valueString.split(',').forEach(innerField => {
                    if (userDict.hasOwnProperty(innerField)) {
                        baseValue += userDict[innerField] + ' ';
                    }
                });

                baseValue = baseValue.trim();
                customFields[field] = baseValue;
            }
        }
        setCustomFields(customFields);
    }
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
      <input
        ref={addPhotoInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handlePhotoFilesSelected}
      />
      {imageUrls.length > 0 && (
        <Box display="flex" flexDirection="column" alignItems="center" width="100%">
          <Box display="flex" flexDirection="column" alignItems="center">
            {imageUrls.map((url, index) => (
              <Box key={index} display="flex" flexDirection="column" alignItems="center" mb={3}>
                <img
                  id={`processed-image-${index}`}
                  src={url}
                  alt={`Product Image ${index}`}
                  width={300}
                  style={{ background: 'white', borderRadius: '2px', border: '2px solid gray' }}
                />
                {uploaderStyle === 1 && (
                  <Typography>Please keep the background removed for product images and re-add it for any photos of you in the product.</Typography>
                )}
                <Button
                  onClick={() => toggleImageAtIndex(index)}
                  sx={{ marginTop: '20px', padding: '5px 10px',  backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' }}}
                  variant="contained"
                >
                  Background ON/OFF
                </Button>
              </Box>
            ))}
          </Box>

          <Button
            variant="outlined"
            onClick={handleAddPhotos}
            disabled={isUploadingPhotos}
            sx={{ mb: 2 }}
          >
            {isUploadingPhotos ? 'Uploading...' : 'Add Photos'}
          </Button>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {/* Shopify Category */}
            <FormControl fullWidth margin="normal" sx={{ width: '80%' }}>
              <InputLabel id="shopify-cat-label">Shopify Category</InputLabel>
              <Select
                labelId="shopify-cat-label"
                label="Shopify Category"
                value={shopifyCat}
                onChange={(e) => setShopifyCat(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
              >
                {SHOPIFY_CATEGORY_NAMES.map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: '0.85rem', whiteSpace: 'normal' }}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* eBay Category — editing disabled for now */}
            {ebayCategories.length > 0 && (
              <TextField
                label="eBay Category"
                value={ebayCat}
                sx={{ width: '80%' }}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
            )}

            {Object.entries(formData).map(([key, value]) => {
              const isCustomKey = false;// key === 'Title';
              const isDescription = false;//key === 'Description';
              // Category editing is disabled for now — see the eBay Category field above.
              const isCategoryField = key === 'Category' || key === 'PrimaryCategory';
              const isDropdown = dynamicOptions.hasOwnProperty(key) && !isCategoryField;

              return (
                <React.Fragment key={key}>
                  {!isDescription && (
                    <FormControl fullWidth margin="normal">
                      {/* <InputLabel id={`${key}-label`}>{key}</InputLabel> */}
                      {isDropdown ? (
                        <Select
                          labelId={`${key}-label`}
                          id={key}
                          name={key}
                          value={translateDropdownOptions(key, value)}
                          onChange={handleDropdownChange}
                          renderValue={(selected) => translateDropdownOptions(key, selected)}
                          sx={{  width: '80%',
                            paddingLeft: '20px',  // Add padding to prevent overflow on the left
                            textAlign: 'left',  // Center text within the select box
                            textOverflow: 'ellipsis', // Handle overflow if text is too long
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'}}
                        >
                          {dynamicOptions[key].map((option, index) => (
                            <MenuItem key={index} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <div>
                        <TextField
                          id={key}
                          name={key}
                          label={key}
                          value={value}
                          onChange={handleChange}
                          multiline
                          rows={0}  // Adjust the number of rows to your needs
                          sx={{ width: '80%' }}
                          InputProps={{
                            readOnly: isCustomKey || isCategoryField,
                            sx: {
                              paddingLeft: '10px',  // Add 10px of padding to the left
                            },
                          }}
                        />
                         { (key === "Price") && (
                           <Button
                           onClick={viewProductPricing}
                           variant="contained"
                           sx={{ backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' }, marginTop: '20px' }}
                         >
                           View Detailed Pricing
                         </Button>
                        )}
                        </div>
                      )}
                     
                    </FormControl>
                  )}
                </React.Fragment>
              );
            })}
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={3} marginTop={2} width="100%">
              { !isLoading && upc && ( <TextField
                name="UPC"
                label="UPC"
                value={upc}
                onChange={(e) => setUPC(e.target.value)}
                multiline
                rows={0}
                sx={{ width: '80%' }}
                InputProps={{
                  sx: {
                    paddingLeft: '10px',
                  },
                }}
              />)}{ !isLoading && sku && (<TextField
                name="SKU"
                label="SKU"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                multiline
                rows={0}
                sx={{ width: '80%' }}
                InputProps={{
                  sx: {
                    paddingLeft: '10px',
                  },
                }}
              />)}      
            </Box>
            { !isLoading && (
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" gap={2} marginTop={3} width="100%">
              <TextField
                label="New Field Name"
                value={newFieldKey}
                onChange={(e) => setNewFieldKey(e.target.value)}
                multiline
                rows={0}
                sx={{ width: '32%' }}
                InputProps={{
                  sx: {
                    paddingLeft: '10px',
                  },
                }}
              />
              <TextField
                label="New Field Value"
                value={newFieldValue}
                onChange={(e) => setNewFieldValue(e.target.value)}
                multiline
                rows={0}
                sx={{ width: '32%' }}
                InputProps={{
                  sx: {
                    paddingLeft: '10px',
                  },
                }}
              />
              <Button
                onClick={addNewField}
                variant="contained"
                sx={{ backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' } }}
              >
                Add New Field
              </Button>
            </Box>
            )}
            {Object.keys(formData).length !== 0 && (
              <>
                {isSubmitting ? (
                  <>
                  {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
                  </>
                ) : (
                  <>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', justifyContent: 'center', marginTop: 2 }}>
                  <Button
                    type="submit"
                    sx={{ padding: '10px 20px', backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' } }}
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {uploaderStyle === 1 ? 'Submit' : 'Save'}
                  </Button>
                  {customerHasShopify(customer) && (shopifyUrl ? (
                   <Button
                   onClick={() => window.open(shopifyUrl, '_blank')}
                   sx={{ padding: '10px 20px', backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' } }}
                   variant="contained"
                   disabled={isSubmitting}
                  >
                  View Shopify Listing
                  </Button>
                  ) : (
                    <Button
                        onClick={handleShopifyPost}
                        sx={{ padding: '10px 20px', backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' } }}
                        variant="contained"
                        disabled={isSubmitting}
                    >
                    Post to Shopify
                    </Button>
                  )
                  )}
                 {customerHasEbay(customer) && (ebayUrl ? (
                   <Button
                   onClick={() => window.open(ebayUrl, '_blank')}
                   sx={{ padding: '10px 20px', backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' } }}
                   variant="contained"
                   disabled={isSubmitting}
                  >
                  View eBay Listing
                  </Button>
                  ) : (
                    <Button
                        onClick={handleEbayPost}
                        sx={{ padding: '10px 20px', backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' } }}
                        variant="contained"
                        disabled={isSubmitting}
                    >
                        Post to eBay
                    </Button>
                  )
                  )}
                 {customerHasEtsy(customer) && (etsyUrl ? (
                   <Button
                   onClick={() => window.open(etsyUrl, '_blank')}
                   sx={{ padding: '10px 20px', backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' } }}
                   variant="contained"
                   disabled={isSubmitting}
                  >
                  View Etsy Listing
                  </Button>
                  ) : (
                    <Button
                        onClick={handleEtsyPost}
                        sx={{ padding: '10px 20px', backgroundColor: '#4751ed', '&:hover': { backgroundColor: '#0f0721' } }}
                        variant="contained"
                        disabled={isSubmitting}
                    >
                        Post to Etsy
                    </Button>
                  )
                  )}
                  {customerHasGoogleMerchant(customer) && (gmcPosted ? (
                    <>
                      <Button
                        onClick={() => window.open(`https://merchants.google.com/mc/products/details?a=${gmcMerchantId}&productId=${productId}`, '_blank')}
                        sx={{ padding: '10px 20px', backgroundColor: '#4285F4', '&:hover': { backgroundColor: '#2b6dda' } }}
                        variant="contained"
                        disabled={isSubmitting}
                      >
                        View in Merchant Center
                      </Button>
                      <Button
                        onClick={handleGoogleMerchantRemove}
                        sx={{ padding: '10px 20px' }}
                        variant="outlined"
                        color="error"
                        disabled={isSubmitting}
                      >
                        Remove from Google Merchant
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleGoogleMerchantPost}
                      sx={{ padding: '10px 20px', backgroundColor: '#4285F4', '&:hover': { backgroundColor: '#2b6dda' } }}
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      Post to Google Merchant
                    </Button>
                  ))}
                  </Box>
                  </>
                )}
              </>
            )}
          </form>
        </Box>
      )}
      {isLoading && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
          <CircularProgress />
          {uploaderStyle === 1 ? (
            <Typography>This might take a few minutes — sit back and get comfy. We’ll be ready in no time!</Typography>
          ) : (
            <>
              {imageUrls.length === 0 && <Typography>Analyzing and enhancing images....</Typography>}
              {imageUrls.length > 0 && <Typography>Determining item specifications and price....</Typography>}
              {!formData && <Typography>Getting more detailed specifications and verifying....</Typography>}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProductEditGeneric;